import React from 'react';
import { AlertTriangle, Clock } from 'lucide-react';

/**
 * Stale badge for leads / opportunities that have exceeded their stage SLA.
 *
 * Props:
 *   record: object with is_stale, days_in_stage, sla_threshold_days
 *   variant: 'pill' (default) | 'inline' | 'corner'
 */
export default function StaleBadge({ record, variant = 'pill' }) {
  if (!record) return null;
  const { is_stale, days_in_stage, sla_threshold_days } = record;

  // If not stale but we have a high days_in_stage (≥50% of threshold) show a yellow "ageing" hint
  const ageing =
    !is_stale &&
    days_in_stage != null &&
    sla_threshold_days != null &&
    sla_threshold_days < 9999 &&
    days_in_stage >= Math.ceil(sla_threshold_days * 0.7);

  if (!is_stale && !ageing) return null;

  const color = is_stale
    ? 'bg-red-50 text-red-700 border-red-200'
    : 'bg-amber-50 text-amber-700 border-amber-200';
  const Icon = is_stale ? AlertTriangle : Clock;
  const label = is_stale ? 'STALE' : 'AGEING';
  const tip = is_stale
    ? `${days_in_stage}d in stage · SLA ${sla_threshold_days}d`
    : `${days_in_stage}d in stage · SLA ${sla_threshold_days}d`;

  if (variant === 'corner') {
    return (
      <span
        title={tip}
        className={`inline-flex items-center gap-1 absolute top-1.5 right-1.5 text-[9px] font-display font-bold uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${color}`}
        data-testid={`stale-badge-${record.id || ''}`}
      >
        <Icon size={10} /> {label}
      </span>
    );
  }
  if (variant === 'inline') {
    return (
      <span
        title={tip}
        className={`inline-flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-wider ${
          is_stale ? 'text-red-700' : 'text-amber-700'
        }`}
        data-testid={`stale-badge-${record.id || ''}`}
      >
        <Icon size={11} /> {days_in_stage}d / {sla_threshold_days}d
      </span>
    );
  }
  return (
    <span
      title={tip}
      className={`inline-flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${color}`}
      data-testid={`stale-badge-${record.id || ''}`}
    >
      <Icon size={10} /> {label} · {days_in_stage}d
    </span>
  );
}
